import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useServerView, activeFilters, ListActions, ListPrint, clamp2 } from "@/components/list/ListView";
import { QrFilterBar } from "@/components/quality/QrFilterBar";
import { PSummary } from "@/components/print/PrintDoc";
import { pl } from "@/components/quality/QrTools";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ArchiveButton } from "@/components/ArchiveDialog";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { ClipboardList, Loader2, Plus, Search, CalendarClock, Download } from "lucide-react";
import { ActionFormDialog, LE_GUESTS } from "@/components/le/ActionUI";
import { FollowupCard } from "@/components/le/FollowupCard";

const FILTERS = [["open", "Åbne"], ["overdue", "Overskredne"], ["closed", "Lukkede"], ["all", "Alle"]];

function Card({ children, className = "" }) { return <div className={`bg-white rounded-2xl border border-[#EBE8E3] ${className}`}>{children}</div>; }
function Stat({ label, value, tone }) { return <Card className="p-4"><p className="text-xs text-stone-500">{label}</p><p className={`text-2xl font-semibold mt-0.5 ${tone}`}>{value}</p></Card>; }

export default function LeActionPlan() {
  const nav = useNavigate();
  const sv = useServerView("/le-view/actions", { defaults: { visning: "open" }, exportPath: "/le-view/actions/export.xlsx" });
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const actions = sv.rows;
  const loading = sv.data === null;
  const counts = sv.data?.counts || { open: 0, overdue: 0, closed: 0 };
  const visning = sv.getArr("visning");
  const curView = visning.length === 1 ? visning[0] : "all";
  const setView = (v) => sv.setParam({ visning: v === "all" ? [] : [v] });

  const load = () => { sv.reload(); api.get("/users").then((us) => setUsers(us.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); };
  useEffect(() => { api.get("/users").then((us) => setUsers(us.data)).catch(() => {}); }, []);

  const exportExcel = async () => {
    try {
      const r = await api.get("/le/actions/export/excel", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "handlingsplan.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Handlingsplan eksporteret");
    } catch (e) { toast.error("Kunne ikke eksportere"); }
  };

  const shown = actions;
  const filters = activeFilters(sv, { status: "Status", ansvarlig: "Ansvarlig", moede: "Møde", punkt: "Dagsordenpunkt" });
  filters.unshift(["Visning", curView && curView !== "all" ? (({ open: 'Åbne', overdue: 'Overskredne', closed: 'Lukkede' })[curView] || curView) : "Alle"]);

  return (
    <div className="space-y-6" data-testid="le-actionplan-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="label-caps flex items-center gap-2 text-[#806B61]"><ClipboardList className="h-3.5 w-3.5" /> Ledelse · Ledelsesevaluering</p>
          <h1 className="text-4xl font-semibold text-[#1A1919] mt-1">Handlingsplan</h1>
          <p className="text-stone-500 mt-2">Levende handlingsplan — hver handling følges gennem flere møder.</p>
        </div>
        <div className="flex items-center gap-2">
          <ListActions onExport={sv.exportXlsx} more={[]}
            primary={<Button data-testid="le-new-action-btn" onClick={() => setCreateOpen(true)} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]"><Plus className="h-4 w-4 mr-1.5" /> Ny handling</Button>}>
            <ArchiveButton kind="le_actions" onRestored={load} /></ListActions>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 max-w-lg">
        <Stat label="Åbne" value={counts.open} tone="text-[#806B61]" />
        <Stat label="Overskredne" value={counts.overdue} tone="text-[#A83232]" />
        <Stat label="Lukkede" value={counts.closed} tone="text-[#5f6b4a]" />
      </div>

      <QrFilterBar search={sv.search} setSearch={sv.onSearch} hideQuick hideSaved placeholder="Søg nr., tekst, ansvarlig, dagsordenpunkt…" shown={shown.length} total={sv.data?.total ?? 0} onReset={sv.reset} leadActive={curView !== "open"} match={sv.match} setMatch={sv.setMatch}
        filters={[sv.mkFilter("status", "Status"), sv.mkFilter("ansvarlig", "Ansvarlig"), sv.mkFilter("moede", "Møde"), sv.mkFilter("punkt", "Dagsordenpunkt")]} period={sv.period}
        lead={<div className="inline-flex rounded-xl border border-[#EBE8E3] bg-white p-0.5 h-9 items-center">{FILTERS.map(([v, l]) => (
          <button key={v} type="button" data-testid={`le-filter-${v}`} onClick={() => setView(v)} className={`px-3 h-full rounded-[10px] text-sm font-medium ${curView === v ? "bg-[#A83232] text-white" : "text-stone-600"}`}>{l}</button>))}</div>} />
      <ListPrint kicker="BSM A/S · Ledelsesevaluering" title="Handlingsplan" noun="handlinger" filters={filters}
        kpis={[{ label: "Handlinger i udvalget", value: shown.length }, { label: "Overskredne", value: counts.overdue, dark: true }, { label: "Åbne", value: counts.open }, { label: "Lukkede", value: counts.closed }]}
        summary={<PSummary>{pl(shown.length, "handling", "handlinger")} i udvalget, heraf {pl(counts.overdue, "overskredet", "overskredne")} og {pl(counts.closed, "lukket", "lukkede")}.</PSummary>}
        cols={[{ label: "Nr.", key: "action_no", w: "26mm", cls: "nowrap" }, { label: "Handling", key: "description" }, { label: "Ansvarlig", key: "resp2", w: "34mm" }, { label: "Frist", key: "frist", w: "26mm", cls: "nowrap" }, { label: "Status", key: "status", w: "24mm", cls: "nowrap" }]}
        rows={shown.map((a) => ({ ...a, frist: daDate(a.deadline), resp2: clamp2(a.responsible_name) }))} />

      {loading ? <div className="flex items-center gap-2 text-stone-400"><Loader2 className="h-4 w-4 animate-spin" /> Indlæser…</div> : shown.length === 0 ? (
        <Card className="p-8 text-center text-stone-400" data-testid="le-actions-empty">Ingen handlinger matcher filtrene.</Card>
      ) : (
        <div className="space-y-3" data-testid="le-actionplan-cards">
          {shown.map((a) => {
            const ms = a.milestones || [];
            const msDone = ms.filter((m) => m.done).length;
            return <FollowupCard key={a.id} action={a} users={users} onChanged={load}
              badge={ms.length > 0 ? <span className="inline-flex items-center gap-1 text-[11px] text-[#806B61]"><CalendarClock className="h-3 w-3" />{msDone}/{ms.length} milepæle</span> : null} />;
          })}
        </div>
      )}

      {createOpen && <ActionFormDialog open={createOpen} onOpenChange={setCreateOpen} users={users} onSaved={load} />}
    </div>
  );
}
